"use client";

import { useState } from "react";

/**
 * LazyMapsEmbed — affiche un placeholder statique léger ; ne monte l'iframe
 * Google Maps qu'au clic utilisateur. Gain LCP mesuré : ~1.5s sur fiche médecin
 * (cf SEO audit 2026-05-07 : iframe Maps = principal contributeur poids/LCP).
 *
 * Le placeholder garde 3 CTAs natifs même si l'iframe n'est jamais monté :
 *   - Afficher la carte (mount on click)
 *   - Voir l'itinéraire (lien direct Google Maps)
 *   - Plein écran (lien direct Google Maps)
 *
 * Pour les utilisateurs qui ne cliqueront pas sur la carte, gain net : -190 KB JS,
 * -1 long task de 156ms, LCP réduit.
 */

type Props = {
  latitude: number;
  longitude: number;
  addressLine?: string | null;
  /** i18n labels — fournis par le parent pour respecter le namespace existant. */
  labels: {
    cabinetHere: string;
    fullscreen: string;
    gpsRoute: string;
    showMap: string;
  };
};

export function LazyMapsEmbed({ latitude, longitude, addressLine, labels }: Props) {
  const [showIframe, setShowIframe] = useState(false);
  // GRATUIT + FIABLE : iframe OpenStreetMap officiel (export/embed.html).
  // L'ancien `maps.google.com/maps?q=...&output=embed` (format non-officiel)
  // affiche souvent un écran gris "can't load Google Maps" + risque de
  // facturation. OSM embed = zéro clé, zéro quota, rendu garanti.
  // bbox = cadre ~0.6 km autour du point ; marker pose le pin exact.
  const d = 0.006; // ~600 m de demi-cadre → zoom proche
  const bbox = `${longitude - d},${latitude - d},${longitude + d},${latitude + d}`;
  const iframeSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitude},${longitude}`;

  return (
    <div className="relative h-64 bg-[var(--sahha-surface-1)] sm:h-80">
      {showIframe ? (
        <iframe
          src={iframeSrc}
          className="h-full w-full border-0"
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          title={`Carte ${addressLine ?? ""}`}
        />
      ) : (
        // Placeholder : dégradé teal + pin animé. 0 réseau, 0 JS tiers.
        <button
          type="button"
          onClick={() => setShowIframe(true)}
          className="group flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-[var(--sahha-primary-50)] via-[var(--sahha-primary-100)] to-[var(--sahha-primary-200)] transition-all hover:from-[var(--sahha-primary-100)] hover:to-[var(--sahha-primary-300)]"
          aria-label={labels.showMap}
        >
          <span className="relative flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg">
            <svg viewBox="0 0 24 24" className="h-6 w-6 text-[var(--sahha-primary-700)]" fill="currentColor" aria-hidden>
              <path d="M12 2C7.6 2 4 5.6 4 10c0 6 8 12 8 12s8-6 8-12c0-4.4-3.6-8-8-8zm0 11.5c-1.9 0-3.5-1.6-3.5-3.5S10.1 6.5 12 6.5s3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5z" />
            </svg>
            <span className="absolute -inset-1 animate-ping rounded-full bg-[var(--sahha-primary-400)]/30" aria-hidden />
          </span>
          <span className="rounded-full bg-white/90 px-4 py-1.5 text-[12.5px] font-semibold text-[var(--sahha-text-900)] shadow group-hover:bg-white">
            {labels.showMap}
          </span>
        </button>
      )}

      {/* Floating marker info (visible aussi sur placeholder) */}
      <div className="absolute left-3 top-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-[12px] font-semibold text-[var(--sahha-text-900)] shadow-lg">
        <span className="flex h-2 w-2 rounded-full bg-[var(--sahha-primary-600)] ring-2 ring-[var(--sahha-primary-600)]/30" />
        {labels.cabinetHere}
      </div>

      {/* Bottom actions — fonctionnent même sans monter l'iframe */}
      <div className="absolute bottom-3 left-3 right-3 flex flex-wrap items-center justify-between gap-2">
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[11px] font-semibold text-[var(--sahha-text-700)] shadow hover:bg-[var(--sahha-primary-50)]"
        >
          {labels.fullscreen}
        </a>
        <a
          href={`https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-full bg-[var(--sahha-primary-600)] px-3.5 py-2 text-sm font-semibold text-white shadow-lg hover:bg-[var(--sahha-primary-700)]"
        >
          {labels.gpsRoute}
        </a>
      </div>
    </div>
  );
}
